import type { IUpdateReservationDto } from '@/api/services/reservations.service';
import type { IReservation } from '@/interfaces/IReservation';

export type IReservationNotesIdentity = Pick<
  IReservation,
  'id' | 'restaurantId' | 'branchId'
>;
export type IReservationNotesPatch = Pick<
  IUpdateReservationDto,
  'notesInternal' | 'tags'
>;
interface IDraft {
  row: IReservation;
  notes: string;
  tags: string[];
  notesVersion: number;
  tagsVersion: number;
  sendingNotes: boolean;
  sendingTags: boolean;
  retryNotes: boolean;
  retryTags: boolean;
  references: number;
  running: Promise<boolean> | null;
  error: unknown;
  cancelled: boolean;
}
export interface IReservationNotesSnapshot {
  row: IReservation;
  notes: string;
  tags: string[];
  dirty: boolean;
  saving: boolean;
  error: unknown;
}
export interface IReservationNotesEvent {
  key: string;
  kind: 'change' | 'saved' | 'error';
  row: IReservation;
  error?: unknown;
}
export function reservationNotesKey(row: IReservationNotesIdentity): string {
  return `${row.restaurantId}:${row.branchId}:${row.id}`;
}
const sameTags = (left: string[], right: string[] | null) =>
  JSON.stringify(left) === JSON.stringify(right ?? []);

/** Plain state survives a departing view; only mounted subscribers update Vue state. */
export function createReservationNotesQueue(
  persist: (
    identity: IReservationNotesIdentity,
    patch: IReservationNotesPatch
  ) => Promise<IReservation>
) {
  const drafts = new Map<string, IDraft>();
  const listeners = new Set<(event: IReservationNotesEvent) => void>();
  function changed(draft: IDraft) {
    return (
      draft.retryNotes ||
      draft.retryTags ||
      draft.notes !== (draft.row.notesInternal ?? '') ||
      !sameTags(draft.tags, draft.row.tags)
    );
  }
  function notify(
    key: string,
    draft: IDraft,
    kind: IReservationNotesEvent['kind'] = 'change'
  ) {
    for (const listener of listeners)
      listener({ key, kind, row: draft.row, error: draft.error });
  }
  function prune(key: string, draft: IDraft) {
    if (
      drafts.get(key) === draft &&
      !draft.references &&
      !draft.running &&
      !changed(draft)
    )
      drafts.delete(key);
  }
  function accept(row: IReservation) {
    const key = reservationNotesKey(row);
    const draft = drafts.get(key);
    if (!draft) return;
    const keepNotes =
      draft.sendingNotes ||
      draft.retryNotes ||
      draft.notes !== (draft.row.notesInternal ?? '');
    const keepTags =
      draft.sendingTags ||
      draft.retryTags ||
      !sameTags(draft.tags, draft.row.tags);
    // Reads and other actions may refresh the row, but cannot acknowledge our in-flight fields.
    draft.row = {
      ...row,
      ...(draft.sendingNotes ? { notesInternal: draft.row.notesInternal } : {}),
      ...(draft.sendingTags ? { tags: draft.row.tags } : {}),
    };
    if (!keepNotes) draft.notes = row.notesInternal ?? '';
    if (!keepTags) draft.tags = [...(row.tags ?? [])];
    if (!changed(draft) && !draft.running) draft.error = null;
    notify(key, draft);
  }
  function bind(row: IReservation) {
    const key = reservationNotesKey(row);
    let draft = drafts.get(key);
    if (!draft) {
      draft = {
        row,
        notes: row.notesInternal ?? '',
        tags: [...(row.tags ?? [])],
        notesVersion: 0,
        tagsVersion: 0,
        sendingNotes: false,
        sendingTags: false,
        retryNotes: false,
        retryTags: false,
        references: 0,
        running: null,
        error: null,
        cancelled: false,
      };
      drafts.set(key, draft);
    } else accept(row);
    draft.references++;
    return key;
  }
  function release(key: string) {
    const draft = drafts.get(key);
    if (!draft) return;
    draft.references = Math.max(0, draft.references - 1);
    prune(key, draft);
  }
  function edit(key: string, value: { notes?: string; tags?: string[] }) {
    const draft = drafts.get(key);
    if (!draft) return;
    if (value.notes !== undefined && value.notes !== draft.notes) {
      draft.notes = value.notes;
      draft.notesVersion++;
    }
    if (value.tags !== undefined && !sameTags(value.tags, draft.tags)) {
      draft.tags = [...value.tags];
      draft.tagsVersion++;
    }
    draft.error = null;
    notify(key, draft);
  }
  function snapshot(key: string | null): IReservationNotesSnapshot | null {
    const draft = key ? drafts.get(key) : undefined;
    return draft
      ? {
          row: draft.row,
          notes: draft.notes,
          tags: [...draft.tags],
          dirty: changed(draft) || !!draft.running,
          saving: !!draft.running,
          error: draft.error,
        }
      : null;
  }
  function flush(key: string): Promise<boolean> {
    const draft = drafts.get(key);
    if (!draft) return Promise.resolve(true);
    if (draft.running) return draft.running;
    if (!changed(draft)) return Promise.resolve(true);
    draft.error = null;
    // Install the promise before starting work so concurrent flushes join the same drain.
    const running = Promise.resolve().then(async () => {
      try {
        while (!draft.cancelled && changed(draft)) {
          const notesVersion = draft.notesVersion;
          const tagsVersion = draft.tagsVersion;
          const patch: IReservationNotesPatch = {};
          if (
            draft.retryNotes ||
            draft.notes !== (draft.row.notesInternal ?? '')
          )
            patch.notesInternal = draft.notes || null;
          if (draft.retryTags || !sameTags(draft.tags, draft.row.tags))
            patch.tags = [...draft.tags];
          draft.sendingNotes = patch.notesInternal !== undefined;
          draft.sendingTags = patch.tags !== undefined;
          const { id, restaurantId, branchId } = draft.row;
          const result = await persist({ id, restaurantId, branchId }, patch);
          if (draft.cancelled) return false;
          draft.row = result;
          // An edit (including reverting to the old baseline) after dispatch remains pending.
          if (notesVersion === draft.notesVersion)
            draft.notes = result.notesInternal ?? '';
          if (tagsVersion === draft.tagsVersion)
            draft.tags = [...(result.tags ?? [])];
          draft.sendingNotes = false;
          draft.sendingTags = false;
          draft.retryNotes = false;
          draft.retryTags = false;
          notify(key, draft, 'saved');
        }
        return !draft.cancelled;
      } catch (error) {
        draft.retryNotes ||= draft.sendingNotes;
        draft.retryTags ||= draft.sendingTags;
        draft.error = error;
        if (!draft.cancelled) notify(key, draft, 'error');
        return false;
      } finally {
        draft.sendingNotes = false;
        draft.sendingTags = false;
        draft.running = null;
        if (!draft.cancelled) notify(key, draft);
        prune(key, draft);
      }
    });
    draft.running = running;
    notify(key, draft);
    return running;
  }
  async function flushAll(keys: Iterable<string>) {
    // Live Sets also include a selection made while a previous reservation is draining.
    const targets = keys instanceof Set ? keys : new Set(keys);
    do {
      for (const key of targets) if (!(await flush(key))) return false;
    } while ([...targets].some((key) => snapshot(key)?.dirty));
    return true;
  }
  function subscribe(listener: (event: IReservationNotesEvent) => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }
  function clear() {
    for (const [key, draft] of drafts) {
      draft.cancelled = true;
      drafts.delete(key);
      notify(key, draft);
    }
  }
  return {
    bind,
    release,
    accept,
    edit,
    snapshot,
    flush,
    flushAll,
    subscribe,
    clear,
  };
}
export type IReservationNotesQueue = ReturnType<
  typeof createReservationNotesQueue
>;
